import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2, Clock, Loader2 } from "lucide-react";

const LEAD_KEY = "sz_sayisal_lead_id";

export default function DiagnosticSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState(null);        // {status:"item"|"sequence_complete"|null}
  const [nextLoading, setNextLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [confidence, setConfidence] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const leadHeader = useCallback(() => {
    const lead = localStorage.getItem(LEAD_KEY);
    return lead ? { "X-Anonymous-Lead-Id": lead } : {};
  }, []);

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/sayisal/diagnostic-sessions/${sessionId}`, {
        headers: leadHeader(),
      });
      setSession(res.data);
      return res.data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  }, [sessionId, leadHeader]);

  const fetchNext = useCallback(async () => {
    setNextLoading(true);
    try {
      const res = await api.get(
        `/sayisal/diagnostic-sessions/${sessionId}/next`,
        { headers: leadHeader() },
      );
      setNext(res.data);
      setAnswer("");
      setConfidence("");
    } catch (e) {
      // 409 session_not_ready is expected for insufficient_package
      const code = e?.response?.data?.detail?.code;
      if (code !== "session_not_ready") toast.error(formatApiError(e));
      setNext({ status: "sequence_complete" });
    } finally {
      setNextLoading(false);
    }
  }, [sessionId, leadHeader]);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      setLoading(false);
      if (s && s.status === "ready") await fetchNext();
    })();
  }, [fetchSession, fetchNext]);

  const submit = async () => {
    if (!next || next.status !== "item") return;
    const item = next.item;
    setSubmitting(true);
    try {
      const raw =
        item.item_type === "no_response" || !answer
          ? { kind: "no_answer" }
          : { kind: item.response_schema.kind, value: answer };
      await api.post("/sayisal/attempts", {
        session_id: sessionId,
        item_ref: item.item_id,
        package_id: next.package_id,
        package_version: next.package_version,
        raw_outcome: raw,
        confidence_band: confidence || null,
        anonymous_lead_id: localStorage.getItem(LEAD_KEY),
      });
      await fetchNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post(
        `/sayisal/diagnostic-sessions/${sessionId}/complete`,
        {},
        { headers: leadHeader() },
      );
      navigate("/tanilama/sonuc");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="diagnostic-session-loading">
        <Clock className="w-6 h-6 text-zinc-400 animate-pulse" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="diagnostic-session-notfound">
        <PublicHeader />
        <main className="flex-1 max-w-2xl mx-auto px-5 py-24 text-center">
          <h1 className="font-display text-2xl font-bold text-zinc-900">Oturum bulunamadı.</h1>
          <Link to="/tanilama" className="inline-block mt-6 text-[#1E3A8A] underline">
            Yeni tanılama başlat
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const isReady = session.status === "ready";
  const isInsufficient = session.status === "insufficient_package";
  const hasItem = isReady && next && next.status === "item";
  const sequenceDone = isReady && next && next.status === "sequence_complete";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="diagnostic-session-page">
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-5 lg:px-8 py-16 w-full">
        <div className="text-xs font-mono text-zinc-400">Oturum: {session.id.slice(0, 8)}</div>
        <h1 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-zinc-900">
          {session.tier} · {session.subject_scope.join(" · ")}
        </h1>

        {isInsufficient && (
          <div
            className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-6"
            data-testid="session-status-insufficient"
          >
            <AlertCircle className="w-6 h-6 text-amber-600" />
            <h2 className="mt-3 font-display font-semibold text-lg text-zinc-900">
              Bu ders için tanılama içeriği henüz aktif değil.
            </h2>
            <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
              Eksik dersler: <strong>{(session.missing_subjects || []).join(", ") || "-"}</strong>.
              İçerik paketleri bağımsız uzmanlık akışında değerlendirmeden geçtikçe bu ders açılır.
            </p>
          </div>
        )}

        {hasItem && (
          <div
            className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6"
            data-testid="session-runtime-item"
          >
            <div className="text-xs font-mono text-zinc-400 mb-2">
              #{next.item.sequence_index} · {next.item.subject} · {next.item.item_type}
            </div>
            <pre className="whitespace-pre-wrap text-sm text-zinc-800 leading-relaxed">
              {JSON.stringify(next.item.display, null, 2)}
            </pre>

            {next.item.item_type === "multiple_choice" && (
              <div className="mt-4 flex flex-col gap-2" data-testid="runtime-choices">
                {(next.item.response_schema.choices || []).map((c, idx) => (
                  <label key={idx} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="mc"
                      value={c}
                      checked={answer === c}
                      onChange={() => setAnswer(c)}
                      data-testid={`runtime-choice-${idx}`}
                    />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            )}
            {(next.item.item_type === "short_numeric" || next.item.item_type === "short_text") && (
              <input
                type={next.item.item_type === "short_numeric" ? "text" : "text"}
                inputMode={next.item.item_type === "short_numeric" ? "numeric" : "text"}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                data-testid="runtime-answer-input"
                className="mt-4 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm"
                placeholder="Cevabın"
              />
            )}

            <div className="mt-6">
              <div className="text-xs text-zinc-500 mb-2">Bu cevaptan ne kadar eminsin?</div>
              <div className="flex gap-2">
                {["low", "medium", "high"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setConfidence(c)}
                    data-testid={`runtime-confidence-${c}`}
                    className={`px-3 py-1.5 rounded-full text-xs border ${
                      confidence === c
                        ? "border-[#1E3A8A] bg-[#1E3A8A] text-white"
                        : "border-zinc-300 text-zinc-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="runtime-submit-btn"
              className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11 px-6"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Gönder ve Devam
            </Button>
          </div>
        )}

        {sequenceDone && (
          <div
            className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6"
            data-testid="session-sequence-complete"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <h2 className="mt-3 font-display font-semibold text-lg text-zinc-900">
              Bu oturum için sırayı tamamladın.
            </h2>
            <p className="mt-2 text-sm text-zinc-700">
              Şimdi ham sonuç ekranına geçebilirsin. Sayısal içerik motoru aktif olduğunda burada
              gerçek yorumlar da görüneceğiz.
            </p>
            <Button
              onClick={complete}
              disabled={completing}
              data-testid="complete-session-btn"
              className="mt-4 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11 px-6"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sonuç Ekranına Geç
            </Button>
          </div>
        )}

        {nextLoading && !hasItem && !sequenceDone && (
          <div className="mt-8 text-sm text-zinc-500" data-testid="runtime-loading">
            Bir sonraki soru yükleniyor…
          </div>
        )}

        <div className="mt-10">
          <Link to="/tanilama" data-testid="restart-diagnostic-link" className="text-sm text-zinc-500">
            Yeniden başlat
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
